import React, { useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CallItem } from "../../../types/grants";
import { GrantsLabelItem, ArticleLabelMap } from "../../../services/grantsApi";
import { BarometerIcon, BarometerModal } from "./barometer";
import { CallBarometerData } from "../../../hooks/useBarometerBatch";
import { useCallsColumnConfig } from "./useCallsColumnConfig";
import { RenderContext, ColumnId, ColumnDef } from "./callsColumnRegistry";
import ReportCallModal from "./ReportCallModal";

/**
 * Fields that support server-side sorting.
 * Must match ALLOWED_SORT_FIELDS in backend find-all-calls.dto.ts
 * and SORT_FIELD_MAP in grant-call.service.ts.
 */
export type SortField = "title" | "startDate" | "endDate" | "createdAt";
export type SortDirection = "ASC" | "DESC";

/** Default sort direction for first click on each field */
const DEFAULT_DIRECTION: Record<SortField, SortDirection> = {
  title: "ASC",
  startDate: "ASC",
  endDate: "ASC",
  createdAt: "DESC",
};

const SORT_FIELDS: SortField[] = ["title", "startDate", "endDate", "createdAt"];

interface CallsTableProps {
  calls: CallItem[];
  isAdmin?: boolean;
  onEdit?: (call: CallItem) => void;
  /** Hide status column when only one status is selected in filter */
  hideStatusColumn?: boolean;
  /** Label data for showing labels column */
  labels?: GrantsLabelItem[];
  /** Map of callId -> label assignment */
  articleLabels?: ArticleLabelMap;
  /** Callback when user assigns/removes a label on a call */
  onSetCallLabel?: (callId: string, labelId: string | null) => Promise<unknown>;
  /** Callback when user creates and assigns a label inline */
  onCreateAndAssign?: (name: string, callId: string) => Promise<unknown>;
  /** Callback when user wants to open label management modal */
  onManageLabels?: () => void;
  /** Function to get barometer data for a specific call (from useBarometerBatch) */
  getCallBarometer?: (call: CallItem) => CallBarometerData;
  /**
   * Current user ID — used for per-user localStorage column config.
   * Only used when `visibleColumns`/`allColumns` props are NOT provided
   * (fallback to internal hook, e.g. in tests).
   */
  userId?: string;
  /**
   * Externally controlled visible columns (in order). When provided alongside
   * `allColumns`, the table skips the internal `useCallsColumnConfig` hook —
   * parent page owns the column config and renders the trigger + panel next
   * to FilterBar (spec: docs/specifications/2026-05-27-konfiguracja-widoku-naborow/spec.md).
   */
  visibleColumns?: ColumnDef[];
  /** Externally controlled all configurable columns (in order). See `visibleColumns`. */
  allColumns?: ColumnDef[];
}

/**
 * Wrapper component for BarometerIcon in table rows.
 * Eliminates IIFE pattern and provides ref for focus return.
 */
const CallBarometerIconWrapper: React.FC<{
  call: CallItem;
  getCallBarometer: (call: CallItem) => CallBarometerData;
  onOpenModal: (callId: string, triggerEl: HTMLButtonElement) => void;
}> = ({ call, getCallBarometer, onOpenModal }) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const baro = getCallBarometer(call);
  if (!baro.data && !baro.isLoading && !baro.isError) return null;
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <BarometerIcon
        ref={buttonRef}
        barometerData={baro.data}
        isLoading={baro.isLoading}
        isError={baro.isError}
        onClick={() => {
          if (buttonRef.current) {
            onOpenModal(call.id, buttonRef.current);
          }
        }}
      />
    </span>
  );
};

/**
 * Table component for displaying grant calls in a compact, scannable format.
 * Sorting is handled server-side; this component only manages sort state in URL params.
 * Column visibility and order is managed by useCallsColumnConfig (localStorage per user).
 */
const CallsTable: React.FC<CallsTableProps> = ({
  calls,
  isAdmin = false,
  onEdit,
  hideStatusColumn = false,
  labels,
  articleLabels,
  onSetCallLabel,
  onCreateAndAssign,
  onManageLabels,
  getCallBarometer,
  userId = "anonymous",
  visibleColumns: visibleColumnsProp,
  allColumns: allColumnsProp,
}) => {
  const hasLabelInteraction = !!labels && !!onSetCallLabel && !!onCreateAndAssign && !!onManageLabels;
  const navigate = useNavigate();
  const [barometerModalCallId, setBarometerModalCallId] = useState<string | null>(null);
  const barometerTriggerRef = useRef<HTMLButtonElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Report modal state
  const [reportModalCall, setReportModalCall] = useState<{ id: string; title: string } | null>(null);

  // Column configuration: prefer externally controlled (parent owns hook + panel),
  // fallback to internal hook (legacy / standalone use, e.g. tests).
  // NOTE: Per spec, production pages hoist `useCallsColumnConfig` to render the
  // "Kolumny" trigger next to FilterBar. Internal hook stays as a graceful default.
  const internalConfig = useCallsColumnConfig(userId);
  const visibleColumns = visibleColumnsProp ?? internalConfig.visibleColumns;
  const allColumns = allColumnsProp ?? internalConfig.allColumns;

  // Read sorting state from URL
  const sortField = (searchParams.get("sortBy") as SortField | null) || null;
  const sortDirection = (searchParams.get("sortOrder") as SortDirection) || "ASC";

  const handleSort = (field: string) => {
    if (!SORT_FIELDS.includes(field as SortField)) return;
    const sf = field as SortField;
    const defaultDir = DEFAULT_DIRECTION[sf];
    const newDirection = sortField === sf
      ? (sortDirection === "ASC" ? "DESC" : "ASC")
      : defaultDir;

    setSearchParams((prev) => {
      prev.set("sortBy", sf);
      prev.set("sortOrder", newDirection);
      return prev;
    });
  };

  const ariaSort = (field: string): "ascending" | "descending" | "none" =>
    sortField === field ? (sortDirection === "ASC" ? "ascending" : "descending") : "none";

  // Find the call for barometer modal
  const barometerModalCall = barometerModalCallId
    ? calls.find((c) => c.id === barometerModalCallId) || null
    : null;
  const barometerModalData = barometerModalCall && getCallBarometer
    ? getCallBarometer(barometerModalCall)
    : null;

  // Build render context for column registry
  const renderCtx: RenderContext = {
    isAdmin,
    hideStatusColumn,
    hasLabelInteraction,
    labels: labels || [],
    articleLabels: articleLabels || {},
    onSetCallLabel: onSetCallLabel || (async () => {}),
    onCreateAndAssign: onCreateAndAssign || (async () => {}),
    onManageLabels: onManageLabels || (() => {}),
    getCallBarometer,
    onEdit,
    sortBy: sortField,
    sortOrder: sortDirection,
    onSort: handleSort,
  };

  // Apply context-gating precedence (spec: docs/specifications/2026-05-27-konfiguracja-widoku-naborow/spec.md):
  // - "status" → auto-hide when hideStatusColumn (context override hides regardless of user toggle)
  // - "label"  → forced-on when hasLabelInteraction (context override SHOWS regardless of user toggle,
  //              even if stale localStorage has label: false)
  // - "actions" → has userConfigurable: false, so it's NOT in allColumns/visibleColumns; rendered
  //               separately below in admin-only <th>/<td> blocks.
  // Build from allColumns (not visibleColumns) so we can ADD forced columns the user toggled off.
  const visibleIds = new Set(visibleColumns.map((c) => c.id));
  const isForced = (id: ColumnId): boolean => id === "label" && hasLabelInteraction;

  const contextFilteredColumns = allColumns.filter((col) => {
    if (col.id === "status" && hideStatusColumn) return false;
    if (isForced(col.id)) return true;
    return visibleIds.has(col.id);
  });

  if (calls.length === 0) {
    return null;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-neutral-200">
          <thead className="bg-neutral-50">
            <tr>
              {contextFilteredColumns.map((col) => (
                <th
                  key={col.id}
                  scope="col"
                  data-column-id={col.id}
                  aria-sort={col.sortField ? ariaSort(col.sortField) : undefined}
                  className={`px-3 py-3 text-${col.align ?? "left"} text-xs font-medium uppercase tracking-wider transition-colors ${
                    col.sortField && sortField === col.sortField
                      ? "bg-neutral-100 text-neutral-700"
                      : "text-neutral-500"
                  } ${col.thClass ?? ""}`}
                >
                  {col.renderHeader(renderCtx)}
                </th>
              ))}
              {isAdmin && (
                <th
                  scope="col"
                  className="px-3 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider w-12"
                >
                  Akcje
                </th>
              )}
              {/* "Zgłoś problem" column — visible for all logged-in users, outside isAdmin block */}
              <th
                scope="col"
                className="px-3 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider w-10"
              >
                <span className="sr-only">Zgłoś problem</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-neutral-200">
            {calls.map((call) => (
              <tr
                key={call.id}
                onClick={() => navigate(`/grants/calls/${call.id}`)}
                className="hover:bg-neutral-50 transition-colors cursor-pointer"
              >
                {contextFilteredColumns.map((col) => {
                  // Label cell and barometer need stopPropagation
                  const needsStopProp = col.id === "label";
                  return (
                    <td
                      key={col.id}
                      className={`px-3 py-4 ${col.tdClass ?? ""}`}
                      onClick={needsStopProp ? (e) => e.stopPropagation() : undefined}
                    >
                      {/* Special case: region column appends BarometerIcon outside col.render()
                          to preserve a stable buttonRef needed for focus return after BarometerModal closes.
                          Kept out of the registry pattern intentionally — refactor to ColumnDef.renderSuffix
                          would require Ref forwarding through render context (over-engineering for 1 case). */}
                      {col.id === "region" ? (
                        <div className="flex items-center gap-1">
                          {col.render(call, renderCtx)}
                          {getCallBarometer && (
                            <CallBarometerIconWrapper
                              call={call}
                              getCallBarometer={getCallBarometer}
                              onOpenModal={(callId, ref) => {
                                barometerTriggerRef.current = ref;
                                setBarometerModalCallId(callId);
                              }}
                            />
                          )}
                        </div>
                      ) : (
                        col.render(call, renderCtx)
                      )}
                    </td>
                  );
                })}
                {isAdmin && (
                  <td className="px-3 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit?.(call);
                      }}
                      className="text-neutral-400 hover:text-neutral-600"
                      title="Edytuj"
                      aria-label={`Edytuj nabór ${call.title}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-5 h-5"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                        />
                      </svg>
                    </button>
                  </td>
                )}
                {/* "Zgłoś problem" action — visible for all logged-in users, OUTSIDE isAdmin block */}
                <td className="px-3 py-4 whitespace-nowrap text-right">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation(); // prevent row click → navigate
                      setReportModalCall({ id: call.id, title: call.title });
                    }}
                    className="text-neutral-400 hover:text-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
                    title="Zgłoś problem"
                    aria-label={`Zgłoś problem z naborem ${call.title}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                      />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Barometer modal for calls list */}
      {barometerModalData?.data && (
        <BarometerModal
          isOpen={!!barometerModalCallId}
          onClose={() => setBarometerModalCallId(null)}
          districts={barometerModalData.data.districts}
          year={barometerModalData.data.year}
          voivodeship={barometerModalData.data.voivodeship}
          triggerRef={barometerTriggerRef}
        />
      )}

      {/* Report problem modal */}
      {reportModalCall && (
        <ReportCallModal
          callId={reportModalCall.id}
          callTitle={reportModalCall.title}
          isOpen={true}
          onClose={() => setReportModalCall(null)}
        />
      )}
    </>
  );
};

export default CallsTable;
